import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { useConfiguration } from 'common/hooks/useConfiguration';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { DisabledWhenPolicyExceeded } from '../../../../__legacy__/containers/DisabledWhenPolicyExceeded';
import { EnableFor } from '../../../../__legacy__/containers/EnableForRole';
import { downloadFileWithCheck } from '../../../../__legacy__/helpers/fileDownload.helpers';
import { ButtonWithLoader } from '../../../../design/buttons/ButtonWithLoader';
import { S_ConfirmationModal } from '../../../../design/modals/confirmationModal/S_ConfirmationModal';
import { ColumnLayout } from '../../../common/layouts/columnLayout/ColumnLayout';
import { IJobDetailsStore } from '../../jobDetails.store';
import { useJobPermissions } from '../../jobPermissions';
import { IJobDownload, JOB_DOWNLOAD_TYPE, JOB_STATUS, JOB_TYPE } from '../../jobs.model';
import { getJobTranslationPrefix } from '../../utils';
import { JobDownloadModal } from '../JobDownloadModal';
import { JOB_START_MODAL_TYPE, JobStartModal } from '../JobStartModal';

enum MODAL_TYPE {
  START = 'start',
  STOP = 'stop',
}

interface IJobHeaderButtonsProps {
  workspaceId?: string;
  jobType: JOB_TYPE;
  job: IJobDetailsStore['job'];
  startJobAsync: () => Promise<void>;
  stopJobAsync: () => Promise<void>;
  downloadJobAsync: (downloadType: JOB_DOWNLOAD_TYPE) => Promise<IJobDownload | null>;
  estimateCostAsync: () => Promise<boolean>;
  startInference: () => void;
  estimatedCost: IJobDetailsStore['estimatedCost'];
  availableFunds: number;
}

export const JobHeaderButtons = observer((props: IJobHeaderButtonsProps): ReactElement => {
  const {
    workspaceId,
    job: { id, isOwner, status, parentTrainingId, isTrainingOwner },
    jobType,
    startJobAsync,
    stopJobAsync,
    downloadJobAsync,
    estimateCostAsync,
    startInference,
    estimatedCost,
    availableFunds,
  } = props;
  const { t } = useTranslator();
  const permissions = useJobPermissions();
  const translationPrefix = getJobTranslationPrefix(jobType);
  const config = useConfiguration();
  const [isLoading, setIsLoading] = useState(false);
  const [visibleModal, setVisibleModal] = useState<MODAL_TYPE | null>(null);
  const [downloadData, setDownloadData] = useState<IJobDownload | null>(null);

  const isSubTraining = jobType === JOB_TYPE.TRAINING && parentTrainingId !== null;

  const handleFetch = (callback: () => Promise<void>) => async (): Promise<void> => {
    setIsLoading(true);
    await callback();
    setIsLoading(false);
  };

  const toggleStartModal = (): void => {
    setVisibleModal((visibility) => (visibility === MODAL_TYPE.START ? null : MODAL_TYPE.START));
  };
  const toggleStopModal = (): void => {
    setVisibleModal((visibility) => (visibility === MODAL_TYPE.STOP ? null : MODAL_TYPE.STOP));
  };

  const startButtonClick = async (): Promise<void> => {
    if (config.appConfig.runLocally) {
      await handleFetch(startJobAsync)();
    } else {
      setIsLoading(true);
      const shouldToggleModal = await estimateCostAsync();
      setIsLoading(false);

      if (shouldToggleModal) {
        toggleStartModal();
      }
    }
  };

  const submitStartModal = async (): Promise<void> => {
    await handleFetch(startJobAsync)();
    toggleStartModal();
  };

  const submitStopModal = async (): Promise<void> => {
    await handleFetch(stopJobAsync)();
    toggleStopModal();
  };

  const initializeDownloadAsync = async (downloadType: JOB_DOWNLOAD_TYPE): Promise<IJobDownload | void> => {
    setIsLoading(true);
    const result = await downloadJobAsync(downloadType);
    if (!result) {
      setIsLoading(false);
    }
    setDownloadData(result);
  };

  const submitDownload = async (): Promise<void> => {
    if (!downloadData) {
      return;
    }

    const downloadUrl = await downloadData.urlGenerator();
    if (downloadUrl) {
      await downloadFileWithCheck(downloadUrl);
    }

    setDownloadData(null);
    setIsLoading(false);
  };

  const cancelDownload = (): void => {
    setDownloadData(null);
    setIsLoading(false);
  };

  const isJobStartAvailable = status === JOB_STATUS.DRAFT || status === JOB_STATUS.STOPPED || status === JOB_STATUS.FAILED;
  const isJobStopAvailable = status === JOB_STATUS.RUNNING || status === JOB_STATUS.EVALUATING || status === JOB_STATUS.WAITING;
  const isJobReady = (status === JOB_STATUS.FINISHED || status === JOB_STATUS.IMPORTED) && !isSubTraining;
  const isInferenceAvailable = workspaceId && id && isJobReady;
  const isJobRestart = !!id;

  return (
    <>
      <ColumnLayout size='sm'>
        <DisabledWhenPolicyExceeded limitType='space'>
          <EnableFor hasPermission={permissions.canRun()}>
            <ButtonWithLoader
              loading={isLoading}
              id='start-model-training-button'
              color='primary'
              title={t(isJobStartAvailable ? `${translationPrefix}.start.text` : `${translationPrefix}.start.not_available`)}
              onClick={() => {
                void startButtonClick();
              }}
              disabled={!isJobStartAvailable}
            >
              {t(`${translationPrefix}.start.button`)}
            </ButtonWithLoader>
          </EnableFor>
        </DisabledWhenPolicyExceeded>

        <EnableFor hasPermission={permissions.canRun()}>
          <ButtonWithLoader
            loading={isLoading}
            id='stop-model-training-button'
            color='primary'
            title={t(isJobStopAvailable ? `${translationPrefix}.stop.text` : `${translationPrefix}.stop.not_available`)}
            onClick={toggleStopModal}
            disabled={!isJobStopAvailable}
          >
            {t(`${translationPrefix}.stop.button`)}
          </ButtonWithLoader>
        </EnableFor>
        <EnableFor hasPermission={permissions.canDownload(jobType === JOB_TYPE.TRAINING ? isOwner : isTrainingOwner)}>
          <ButtonWithLoader
            id='download-model-training-button'
            color='primary'
            loading={isLoading}
            title={t(isJobReady ? 'download.title_model' : 'download.not_available')}
            disabled={!isJobReady}
            onClick={() => void initializeDownloadAsync(JOB_DOWNLOAD_TYPE.MODEL)}
          >
            {t('download.button_model')}
          </ButtonWithLoader>
        </EnableFor>
        <EnableFor hasPermission={permissions.canDownload(jobType === JOB_TYPE.TRAINING ? isOwner : isTrainingOwner)}>
          <ButtonWithLoader
            id='download-result-training-button'
            color='primary'
            loading={isLoading}
            title={t(isJobReady ? 'download.title_results' : 'download.not_available')}
            disabled={!isJobReady}
            onClick={() => void initializeDownloadAsync(JOB_DOWNLOAD_TYPE.RESULTS)}
          >
            {t('download.button_results')}
          </ButtonWithLoader>
        </EnableFor>
        {jobType === JOB_TYPE.TRAINING && (
          <EnableFor hasPermission={permissions.canRun()}>
            <Button
              id='run-evaluation-button'
              title={t(isJobReady ? 'inference.start.button' : 'inference.start.not_available')}
              disabled={!isInferenceAvailable}
              onClick={startInference}
            >
              {t('inference.start.button')}
            </Button>
          </EnableFor>
        )}
      </ColumnLayout>
      <JobStartModal
        isOpen={visibleModal === MODAL_TYPE.START}
        availableFunds={availableFunds}
        estimatedCost={estimatedCost}
        isLoading={isLoading}
        jobType={jobType}
        onSubmit={submitStartModal}
        status={status}
        toggle={toggleStartModal}
        type={isJobRestart ? JOB_START_MODAL_TYPE.RESTART : JOB_START_MODAL_TYPE.START}
      />
      <S_ConfirmationModal
        isOpen={visibleModal === MODAL_TYPE.STOP}
        headerText={t(`${translationPrefix}.stop.text`)}
        submitText={t('confirm')}
        cancelText={t('cancel')}
        onCancel={toggleStopModal}
        toggle={toggleStopModal}
        onSubmit={submitStopModal}
      >
        <p>{t(`${translationPrefix}.stop.info`)}</p>
      </S_ConfirmationModal>
      {downloadData && (
        <JobDownloadModal
          onCancel={cancelDownload}
          onSubmit={submitDownload}
          jobDownload={downloadData}
        />
      )}
    </>
  );
});
